import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Users,
  Zap,
  Coffee,
  Globe,
  Award,
} from "lucide-react";
import {
  WhatWeDoSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
  IndustriesSection,
} from "../components/CommonSections";
import { TEAM_IMAGES } from "@/data/teamImages";
import { CASE_STUDIES } from "@/data/mock";

const LifeAtInfotechPage = () => {
  const culture = [
    {
      icon: Heart,
      title: "People First",
      description:
        "We sponsor certifications, run internal tech talks, and maintain a dedicated L&D budget — because great people need room to keep growing.",
    },
    {
      icon: Zap,
      title: "Innovation Driven",
      description:
        "We run internal hackathons, prototype new tools, and give engineers a real voice in shaping the tech stack — because the best ideas come from the people building the product.",
    },
    {
      icon: Users,
      title: "Collaborative Spirit",
      description:
        "From weekly knowledge-sharing sessions to cross-team retrospectives, we work as one team — regardless of role, location, or seniority.",
    },
    {
      icon: Globe,
      title: "Diverse & Inclusive",
      description:
        "We hire for talent and perspective, not just credentials. Every voice matters here, and we mean that beyond the poster on the wall.",
    },
    {
      icon: Coffee,
      title: "Work-Life Balance",
      description:
        "Flexible hours, remote-friendly culture, and a team that respects your time off. We believe well-rested engineers build better products.",
    },
    {
      icon: Award,
      title: "Growth Opportunities",
      description:
        "Clear progression tracks, mentorship from senior engineers, and access to cutting-edge projects across AI, mobile, cloud, and IoT.",
    },
  ];

  const perks = [
    "Work on cutting-edge projects with high visibility across industries.",
    "Exposure to IoT, AI, Cloud Computing, and global tech ecosystems.",
    "Career growth opportunities with challenging technical and managerial responsibilities.",
    "Learning & development and customized training under subject matter experts.",
    "Health insurance benefits for any medical emergencies.",
    "Flexible remote work options for better work-life balance.",
    "Collaborative and dynamic work environment fostering innovation.",
    "Competitive salary tailored to expertise and experience.",
    "Fun activities, celebrations and outings.",
    "Freedom to express ideas through multiple channels.",
  ];

  const galleryImages = [
    TEAM_IMAGES.img1,
    TEAM_IMAGES.img2,
    TEAM_IMAGES.img3,
    TEAM_IMAGES.img4,
    TEAM_IMAGES.img5,
    TEAM_IMAGES.img11,
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-[#b32126] py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&q=80"
            alt="Team"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Life @ Infotech.Works
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              More than just a workplace - we're a community of passionate
              innovators building the future together.
            </p>
            <Link
              to="/careers"
              className="inline-flex items-center px-8 py-4 bg-[#b32126] text-white font-semibold rounded-xl hover:bg-[#8f1a1f] transition-colors"
            >
              Join Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Culture Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Culture
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The values that define who we are and how we work together
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {culture.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-2xl hover:bg-red-50 transition-colors group"
                >
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center mb-6 shadow-md group-hover:shadow-lg transition-shadow">
                    <IconComponent className="h-7 w-7 text-[#b32126]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Team in Action
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-xl ${
                  index === 0 || index === 5
                    ? "md:col-span-2 md:row-span-2"
                    : ""
                }`}
              >
                <img
                  src={image}
                  alt={`Team moment ${index + 1}`}
                  className="w-full h-full object-cover aspect-video hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perks & Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Perks & Benefits
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We take care of our team so they can focus on doing their best
                work.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {perks.map((perk, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-[#b32126] rounded-full mr-3 flex-shrink-0  mt-2" />
                    <span className="text-gray-700">{perk}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1565514417878-a66a6b0f2c7f?fm=jpg&q=80&w=1200&auto=format&fit=crop"
                alt="Office"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#b32126] text-white p-6 rounded-xl shadow-lg hidden md:block">
                <div className="text-3xl font-bold">4.8/5</div>
                <div className="text-sm text-red-100">
                  Employee Satisfaction
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Employee Testimonials */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              In Their Own Words
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  "A wonderful place to work with supportive managers and helpful colleagues. The company encourages teamwork, continuous learning, and provides great opportunities for personal and professional growth.",
                name: "Hardik Kanjariya",
                role: "Technical Architect",
                image: TEAM_IMAGES.hardik,
              },
              {
                quote:
                  "The collaborative environment here is incredible. I've grown more in 2 years than in my entire previous career.",
                name: "Manisha Kale",
                role: "QA Lead",
                image: TEAM_IMAGES.manisha,
              },
              {
                quote:
                  "Leadership genuinely cares about work-life balance. I can be there for my family without sacrificing my career growth.",
                name: "Kamlesh Haswani",
                role: "Technical Architect",
                image: TEAM_IMAGES.kamlesh,
              },
              
              
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-xl p-8 flex flex-col h-full"
              >
                <p className="text-gray-300 mb-6 italic flex-1">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4 object-cover flex-shrink-0"
                  />
                  <div>
                    <div className="text-white font-semibold">
                      {testimonial.name}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      {/* Success Stories */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real clients across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CASE_STUDIES.slice(0, 3).map((study) => (
              <Link
                key={study.id}
                to={`/resources/case-studies/${study.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#b32126] text-white text-xs font-medium rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                      {study.timeline}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-1">
                    {study.clientType}
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#b32126] transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {study.tagline}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.results.slice(0, 3).map((result, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium"
                      >
                        {result.value} {result.label}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center px-6 py-3 border-2 border-[#b32126] text-[#b32126] font-semibold rounded-lg hover:bg-[#b32126] hover:text-white transition-colors"
            >
              View All Case Studies
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />
      <FAQSection />

      {/* CTA */}
      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Join Our Team?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Explore open positions and become part of our growing family
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-colors"
          >
            View Open Positions
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LifeAtInfotechPage;
